
(() => {
    // AJAX 配置
    const AJAX_CONFIG = {
        loadingClass: 'ajax-loading',
        errorClass: 'ajax-error',
        successClass: 'ajax-success',
        loadingTimeout: 10000, // 10秒超时
        retryAttempts: 3,
        retryDelay: 1000,
    };

    // AJAX 状态管理
    class AjaxStateManager {
        constructor() {
            this.isLoading = false;
            this.currentRequest = null;
            this.retryCount = 0;
            this.errorCount = 0;
            this.secondaryRouting = '';
            this.secondaryParameters = '';
        }

        // 开始加载
        startLoading() {
            this.isLoading = true;
            this.retryCount = 0;
            document.body.classList.add(AJAX_CONFIG.loadingClass);
            this.showLoadingIndicator();
        }

        // 停止加载
        stopLoading() {
            this.isLoading = false;
            document.body.classList.remove(AJAX_CONFIG.loadingClass);
            this.hideLoadingIndicator();
        }

        // 显示加载指示器
        showLoadingIndicator() {
            let indicator = document.getElementById('ajax-loading-indicator');
            if (!indicator) {
                indicator = document.createElement('div');
                indicator.id = 'ajax-loading-indicator';
                indicator.innerHTML = `
                    <div class="ajax-loading-overlay">
                        <div class="ajax-loading-spinner">
                            <div class="spinner"></div>
                            <p>Loading products...</p>
                        </div>
                    </div>
                `;
                document.body.appendChild(indicator);
            }
            indicator.style.display = 'flex';
        }

        // 隐藏加载指示器
        hideLoadingIndicator() {
            const indicator = document.getElementById('ajax-loading-indicator');
            if (indicator) {
                indicator.style.display = 'none';
            }
        }

        // 显示错误信息
        showError(message) {
            this.errorCount++;
            const errorElement = document.getElementById('ajax-error-message');
            if (errorElement) {
                errorElement.textContent = message;
                errorElement.style.display = 'block';
                setTimeout(() => {
                    errorElement.style.display = 'none';
                }, 5000);
            }
        }
    }

    // AJAX 请求管理器
    class AjaxRequestManager {
        constructor(stateManager) {
            this.stateManager = stateManager;
            this.abortController = null;
        }

        async makeRequest(url, options = {}) {
            // 取消之前的请求
            if (this.abortController) {
                this.abortController.abort();
            }

            this.abortController = new AbortController();

            const requestOptions = {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'text/html, application/xhtml+xml',
                    ...options.headers,
                },
                signal: this.abortController.signal,
                ...options,
            };

            try {
                this.stateManager.startLoading();

                const response = await Promise.race([
                    fetch(url, requestOptions),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('请求超时')), AJAX_CONFIG.loadingTimeout)),
                ]);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const html = await response.text();
                this.stateManager.stopLoading();
                return html;
            } catch (error) {
                this.stateManager.stopLoading();

                if (error.name === 'AbortError') {
                    console.log('请求被取消');
                    return null;
                }

                // 重试逻辑
                if (this.stateManager.retryCount < AJAX_CONFIG.retryAttempts) {
                    this.stateManager.retryCount++;
                    console.log(`重试请求 (${this.stateManager.retryCount}/${AJAX_CONFIG.retryAttempts})`);

                    await new Promise((resolve) => setTimeout(resolve, AJAX_CONFIG.retryDelay));
                    return this.makeRequest(url, options);
                }

                this.stateManager.showError(`加载失败: ${error.message}`);
                throw error;
            }
        }

        abort() {
            if (this.abortController) {
                this.abortController.abort();
            }
        }
    }

    // collection 页面管理
    class CollectionPageManager {
        constructor() {
            this.stateManager = new AjaxStateManager();
            this.requestManager = new AjaxRequestManager(this.stateManager);
            this.url = new URL(window.location).toString();
            this.collectionHandle = '';
        }

        // 进入假集合页
        enterFakeCollectionPage(collectionHandle) {
            this.collectionHandle = collectionHandle;
            // 更新页面内容
            let handleQuery = '/' + collectionHandle;
            this.updateWithHtml(handleQuery);
        }

        // 设置delegate代理功能
        setDelegate() {
            // 接管筛选器自定义功能
            const facetFilters = document.querySelector('facet-filters');
            if (facetFilters) {
                facetFilters._delegate_ = facetFilters.delegate; // 记录原本的delegate
                // 移除原有的事件监听器
                facetFilters.delegate.off('change', '[name^="filter."]');
                facetFilters.delegate.off('click', '[data-action="clear-filters"]');
                // 添加新的事件监听器 this.rewriteFacetFilters
                facetFilters.delegate.on('change', '[name^="filter."]', (event, target) => {
                    // 阻止默认行为
                    event.preventDefault();
                    event.stopPropagation();

                    // 获取表单数据 sort_by=manual&filter.p.tag=Easter&filter.p.tag=Colors
                    const formData = new FormData(facetFilters.querySelector('#facet-filters-form'));

                    // 更新页面内容
                    let handleQuery = '/' + this.collectionHandle + '?' + new URLSearchParams(formData).toString();
                    this.updateWithHtml(handleQuery);
                });
                facetFilters.delegate.on('click', '[data-action="clear-filters"]', (event, target) => {
                    // 阻止默认行为
                    event.preventDefault();
                    event.stopPropagation();

                    // 更新页面内容
                    let handleQuery = '/' + this.collectionHandle + '?' + target.value;
                    this.updateWithHtml(handleQuery);
                });
            }

            // 接管排序自定义功能
            const sortByPopover = document.querySelector('sort-by-popover');
            if (sortByPopover) {
                sortByPopover._delegate_ = sortByPopover.delegate; // 记录原本的delegate
                // 移除原有的事件监听器
                sortByPopover.delegate.off('change', '[name="sort_by"]');
                // 添加新的事件监听器
                sortByPopover.delegate.on('change', '[name="sort_by"]', (event, target) => {
                    // 阻止默认行为
                    event.preventDefault();
                    event.stopPropagation();
                    // 关闭popover
                    event.target.open = false;

                    // 更新页面内容
                    let url = new URL(window.location);
                    url.searchParams.set('sort_by', target.value);
                    let handleQuery = '/' + this.collectionHandle + '?' + url.searchParams.toString();
                    this.updateWithHtml(handleQuery);
                });
            }

            // 接管分页自定义功能
            const pagePagination = document.querySelector('page-pagination');
            if (pagePagination) {
                pagePagination._delegate_ = pagePagination.delegate; // 记录原本的delegate
                // 移除原有的事件监听器
                pagePagination.delegate.off('click', 'a');
                // 添加新的事件监听器
                pagePagination.delegate.on('click', 'a', (event, target) => {
                    console.log('pagePagination', event, target);
                    // 阻止默认行为
                    event.preventDefault();
                    event.stopPropagation();

                    // 更新页面内容
                    let url = new URL(window.location);
                    url.searchParams.set('page', target.getAttribute('data-page'));
                    let handleQuery = '/' + this.collectionHandle + '?' + url.searchParams.toString();
                    this.updateWithHtml(handleQuery);
                });
            }
        }

        // 接管筛选器清空自定义功能
        rewriteFacetFiltersClear(event, target) {
            // 阻止默认行为
            event.preventDefault();
            event.stopPropagation();
            // 删除除sort_by外的所有参数
            for (const [name, value] of this.url.entries()) {
                if (name != 'sort_by') this.url.delete(name);
            }
            // 更新页面内容
            this.updateWithFilters();
        }

        // 请求ajax，获取新页面html,更新页面内容
        async updateWithHtml(handleQuery = '') {
            try {
                // 1.获取url
                let url = new URL(this.url);
                let ajaxUrl = new URL(url.origin + '/collections' + handleQuery);

                // 设置显示路由
                window.history.pushState({}, '', ajaxUrl);

                // 2.获取HTML
                let html = await this.requestManager.makeRequest(ajaxUrl.toString());

                // 3.解析HTML
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // 4.更新Filters
                let currentFilters = document.querySelector('.product-facet__aside');
                let newFilters = doc.querySelector('.product-facet__aside');
                let currentFiltersCopy = currentFilters.cloneNode(true); // 备份原来的Filters
                if (currentFilters && newFilters) {
                    currentFilters.innerHTML = newFilters.innerHTML;

                    // 恢复原来的Filters打开状态
                    currentFilters.querySelectorAll('.product-facet__filter-item button').forEach((item) => {
                        let ariaControls = item.getAttribute('aria-controls');
                        let ariaExpanded = currentFiltersCopy
                            .querySelector(`[aria-controls="${ariaControls}"]`)
                            .getAttribute('aria-expanded');
                        item.setAttribute('aria-expanded', ariaExpanded);
                    });
                }

                // 5.更新产品列表
                let currentProductList = document.querySelector('.main-collection-item');
                let newProductList = doc.querySelector('.main-collection-item');
                if (currentProductList && newProductList) {
                    currentProductList.innerHTML = newProductList.innerHTML;
                }

                // 6.更新集合介绍
                let currentImageTxtKateLayout = document.querySelector('.image-txt-kate-layout');
                let newImageTxtKateLayout = doc.querySelector('.image-txt-kate-layout');
                if (currentImageTxtKateLayout && newImageTxtKateLayout) {
                    currentImageTxtKateLayout.innerHTML = newImageTxtKateLayout.innerHTML;

                    if (window.initCollectionDescriptionAccordion) {
                        window.initCollectionDescriptionAccordion(currentImageTxtKateLayout);
                    }
                }

                // 7.滚动到产品列表顶部
                // const productList = document.querySelector('.product-facet__product-list');
                // if (productList) {
                //   productList.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // }

                // 8.更新标题
                let currentTitle = document.querySelector('.heading.h3');
                let newTitle = doc.querySelector('.heading.h3');
                if (currentTitle && newTitle) {
                    currentTitle.innerHTML = newTitle.innerHTML;
                }

                // 9.更新面包屑
                let currentBreadcrumb = document.querySelector('.breadcrumb');
                let newBreadcrumb = doc.querySelector('.breadcrumb');
                if (currentBreadcrumb && newBreadcrumb) {
                    // 检查是否为假集合（通过URL参数或当前状态判断）
                    let isFakeCollection = false;
                    let fakeCollectionName = '';

                    // 检查当前是否有激活的子分类
                    let activeCategory = document.querySelector('.christmas-category-item.active');
                    if (activeCategory) {
                        isFakeCollection = true;
                        fakeCollectionName = activeCategory.querySelector('span').textContent;
                    }

                    if (isFakeCollection) {
                        // 构建假集合的面包屑：Home > 集合名 > 当前子分类标题（使用 Liquid 注入的 __collectionPageData）
                        const data = window.__collectionPageData || {};
                        const rootUrl = data.rootUrl || '/';
                        const homeLabel = data.homeLabel || 'Home';
                        const collectionUrl = data.url || '/collections/all';
                        const collectionLabel = data.handle === 'all' ? (data.allProductsLabel || 'All Products') : (data.title || '');
                        let breadcrumbHTML = `
    <ol class="breadcrumb__list" role="list">
        <li class="breadcrumb__item">
            <a class="breadcrumb__link" href="${rootUrl}">${homeLabel}</a>
        </li>
        <li class="breadcrumb__item">
            <a class="breadcrumb__link" href="${collectionUrl}">
                <span class="breadcrumb__link" aria-current="page">${collectionLabel}</span>
            </a>
        </li>
        <li class="breadcrumb__item">
            <span class="breadcrumb__link" aria-current="page">${newTitle.innerHTML}</span>
        </li>
    </ol>
    `;
                        currentBreadcrumb.innerHTML = breadcrumbHTML;
                    } else {
                        // 真集合：直接使用新页面的面包屑
                        currentBreadcrumb.innerHTML = newBreadcrumb.innerHTML;
                    }
                }

                // 设置delegate
                this.setDelegate();
            } catch (error) {
                console.error('更新产品列表失败:', error);
            }
        }
    }

    // 监听浏览器的 popstate 事件（用户点击返回按钮）
    window.addEventListener('popstate', (event) => {
        // 检查是否为假集合（通过URL参数或当前状态判断）
        let isFakeCollection = false;

        // 检查当前是否有激活的子分类
        let activeCategory = document.querySelector('.christmas-category-item.active');
        if (activeCategory) {
            isFakeCollection = true;
        }

        if (isFakeCollection) {
            // 直接跳转到真集合页面（使用 Liquid 注入的 __collectionPageData）
            const collectionUrl = (window.__collectionPageData && window.__collectionPageData.url) || '/collections/all';
            window.location.href = collectionUrl;
        }
    });

    const fakeCollectionDiv = document.querySelector('[data-is-fake-collection]') || false;
    if (fakeCollectionDiv) {
        // 拦截产品链接点击
        document.addEventListener('click', (event) => {
            const productLink = event.target.closest('a[href*="/products/"]');
            if (
                productLink &&
                !productLink.hasAttribute('data-ajax-disabled') &&
                !!window.$collectionAjax.collectionHandle
            ) {
                event.preventDefault();
                event.stopPropagation();

                const productUrl = productLink.href;
                window.history.pushState({}, '', window.$collectionAjax.url);
                window.location.href = productUrl;
            }
        });
    }

    // 页面加载完成后初始化
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => (window.$collectionAjax = new CollectionPageManager()));
    } else {
        window.$collectionAjax = new CollectionPageManager();
    }
})();
